import { useEffect, useState } from 'react';
import { api, type AcctCorp, type LiquidityDay, type LiquidityResult } from '../lib/api';
import { ErrorBox, money, MoneyInput } from '../components/DataTable';
import { FitBox } from '../components/FitBox';
import { useSticky, useAcctCorp } from '../lib/sticky';
import { ForecastPanel } from './LiquidityForecast';

/**
 * 회계 1단계 D — 유동성 시재(#20). 기획 정본 `plan/accounting.md` 5-D. 계산은 서버(`liquidity.ts`)가 BigInt 로 한다.
 * 모양은 시제예측_SYS 의 DB 시트 그대로(일자가 가로축) — 회계팀이 익힌 배치라서.
 * 🔴 기점은 은행 잔액 파일이다(월초 수기 입력이 없다). 은행 자료가 있는 날은 계산값과의 **차이**를 보여 준다.
 */

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const kstToday = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const num = (v: string | null) => (v === null ? null : Number(v));

/** `cashDiff` 만 화면 계산 칸(실사 − 계산) — 실사가 있는 날은 다음 날이 실사에서 다시 시작하므로, 어긋남을 이 줄로 보인다(#321). */
type RowKey = keyof LiquidityDay | 'cashDiff';
type RowDef = { label: string; key: RowKey; strong?: boolean; tone?: 'diff' | 'ref' | 'actual'; sum?: boolean };
const valueOf = (d: LiquidityDay, k: RowKey): string | null => {
  if (k !== 'cashDiff') return d[k] as string | null;
  if (d.cashCount === null || d.cashClose === null) return null;
  return String(Number(d.cashCount) - Number(d.cashClose));
};
const BANK_ROWS: RowDef[] = [
  { label: '전일잔액', key: 'bankOpen' },
  { label: '입금', key: 'bankIn', sum: true },
  { label: '출금(출납일보)', key: 'bankOut', sum: true },
  { label: '당일잔액(계산)', key: 'bankClose', strong: true },
  { label: '은행 실잔액', key: 'bankActual', tone: 'actual' },
  { label: '차이(실잔액−계산)', key: 'bankDiff', tone: 'diff' },
  { label: '(참고) 은행 출금', key: 'bankOutRef', tone: 'ref', sum: true },
  { label: '(참고) 계좌간 이체 입금', key: 'bankInTransfer', tone: 'ref', sum: true },
  { label: '(참고) 수기 입금(추정) — 입금에 포함', key: 'bankInPlan', tone: 'ref', sum: true },
  { label: '(참고) 입금 예측(청구) — 입금에 포함', key: 'bankInForecast', tone: 'ref', sum: true },
];
const CASH_ROWS: RowDef[] = [
  { label: '전일잔액', key: 'cashOpen' },
  { label: '입금', key: 'cashIn', sum: true },
  { label: '출금', key: 'cashOut', sum: true },
  { label: '당일잔액(계산)', key: 'cashClose', strong: true },
  { label: '실사', key: 'cashCount', tone: 'actual' },
  { label: '차이(실사−계산)', key: 'cashDiff', tone: 'diff' },
];

export function LiquidityTab({ corps }: { corps: AcctCorp[] }) {
  const today = kstToday();
  const [month, setMonth] = useSticky('acct.liq.month', today.slice(0, 7));
  const [corpId, setCorpId] = useAcctCorp();
  const [data, setData] = useState<LiquidityResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [counting, setCounting] = useState(false);
  const [fcOpen, setFcOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.getLiquidity({ month, corpId })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, corpId, nonce]);

  const days = data?.days ?? [];
  const future = (d: string) => data?.lastDataDate === null || (data?.lastDataDate ?? '') < d;
  const monthSum = (k: RowKey) => days.reduce((s, d) => s + (Number(valueOf(d, k)) || 0), 0);
  const noBase = data && data.openBank === null && days.every((d) => d.bankActual === null);
  const minClose = days.filter((d) => d.date >= today && d.bankClose !== null).reduce<LiquidityDay | null>(
    (m, d) => (m === null || Number(d.bankClose) < Number(m.bankClose) ? d : m), null);

  const forecastTip = (date: string) => {
    const list = data?.forecast?.[date];
    return list?.length ? list.map((f) => `${f.cust} ${money(Number(f.amt))}`).join(' · ') : undefined;
  };
  const cell = (d: LiquidityDay, r: RowDef) => {
    const v = num(valueOf(d, r.key));
    if (v === null) return <span className="text-ink-faint">—</span>;
    if (r.tone === 'diff') return v === 0 ? <span className="text-emerald-700">0</span> : <span className="font-medium text-amber-700">{money(v)}</span>;
    if (v === 0 && !r.strong && r.key !== 'bankOpen' && r.key !== 'cashOpen') return <span className="text-ink-faint">0</span>;
    return <span className={v < 0 ? 'font-semibold text-red-600' : ''}>{money(v)}</span>;
  };

  const head = (
    <tr className="border-b text-ink-soft">
      <th className="sticky left-0 z-10 bg-white px-2 py-2 text-left">구분</th>
      {days.map((d) => {
        const wd = new Date(`${d.date}T00:00:00`).getDay();
        return (
          <th key={d.date} className={`whitespace-nowrap px-2 text-right font-medium ${d.date === today ? 'bg-brand-100 font-bold !text-ink' : ''} ${wd === 0 || wd === 6 ? 'text-red-400' : ''}`}>
            {Number(d.date.slice(8))}일({WD[wd]}){future(d.date) && <div className="text-[10px] font-normal text-ink-faint">예측</div>}
          </th>
        );
      })}
      <th className="whitespace-nowrap px-2 text-right">월 합계</th>
    </tr>
  );
  const block = (title: string, rows: RowDef[]) => (
    <>
      <tr className="bg-canvas"><td className="sticky left-0 z-10 bg-canvas px-2 py-1 font-semibold" colSpan={1}>{title}</td><td colSpan={days.length + 1} /></tr>
      {rows.map((r) => (
        <tr key={r.key} className={`border-b ${r.strong ? 'font-semibold' : ''} ${r.tone === 'ref' ? 'text-ink-faint' : ''}`}>
          <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-2 py-1">{r.label}</td>
          {days.map((d) => (
            <td key={d.date} className={`whitespace-nowrap px-2 text-right ${d.date === today ? 'bg-brand-100 font-semibold' : future(d.date) ? 'bg-canvas/40' : ''}`}
              title={r.key === 'bankInForecast' ? forecastTip(d.date) : undefined}>{cell(d, r)}</td>
          ))}
          <td className="whitespace-nowrap px-2 text-right">{r.sum ? money(monthSum(r.key)) : ''}</td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="space-y-3">
      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        <input type="month" className="field w-40" value={month} onChange={(e) => setMonth(e.target.value)} />
        <select className="field w-44" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">전 법인 통합</option>
          {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>)}
        </select>
        <button className="btn-ghost" onClick={() => setCounting((v) => !v)}>잔액 입력(현금·계좌)</button>
        {data?.forecast && (
          <button className="btn-ghost" onClick={() => setFcOpen((v) => !v)}>
            입금 예측 내역·일자조정 ({Object.values(data.forecast).flat().length}){(data.forecastOverdue?.length ?? 0) > 0 && <span className="ml-1 text-amber-700">· 연체 {data.forecastOverdue!.length}</span>}
          </button>
        )}
        {data?.lastDataDate && <span className="text-xs text-ink-faint">은행 자료 {data.lastDataDate} 까지 · 그 뒤는 출납일보로 계산한 예측</span>}
      </div>
      <p className="page-hint">
        당일잔액 = 전일잔액 + 입금 − 출금(출납일보). 오늘까지는 전날 <b>은행 실잔액</b>이 있으면 그 값에서 다시 시작하고(월초 수기 입력 없음), 내일부터는 오늘의 <b>당일잔액(계산)</b>에서 이어갑니다(오늘 실잔액은 장중 값).
        <b> 차이</b>가 0 이 아니면 출납일보에 빠진 출금·입금이 있다는 뜻입니다. 현금은 그 날 <b>실사</b>를 적으면 다음 날이 실사 금액에서 다시 시작합니다. 입금 내역 「수기 입금(추정)」에 넣은 금액은 은행 거래내역이 아직 없는 날의 입금에 더합니다.
        <b> 입금 예측(청구)</b>은 청구완료·입금 전인 청구(부가세 별도 청구액)를 거래처 평균 회수일(최근 12개월 입금 이력)로 날짜를 잡아 오늘 이후·은행 거래내역이 없는 날의 입금에 더합니다(주말은 월요일 · 칸에 마우스를 올리면 거래처). 「입금 예측 내역·일자조정」에서 청구마다 예상 입금일을 바꾸거나 되돌릴 수 있고, 예측일이 지났는데 입금이 안 된 청구는 「연체」로 따로 보이며 입금에는 넣지 않습니다.
        출납일보에 시재계정 없이 적은 계좌간 이체(자금모으기)는, 같은 날 같은 금액이 이 범위 계좌로 들어왔으면 입금에서도 뺍니다.
      </p>
      {counting && <CashCountForm corps={corps} defaultCorp={corpId} onSaved={() => setNonce((n) => n + 1)} />}
      {fcOpen && data && <ForecastPanel data={data} corps={corps} onChanged={() => setNonce((n) => n + 1)} />}
      {error && <ErrorBox message={error} />}
      {noBase && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          이 달에는 은행 잔액 자료가 없어 기점을 잡을 수 없습니다. 「은행 파일 올리기」에서 잔액조회 파일을 올려 주세요.
        </p>
      )}
      {!noBase && data && data.openBank === null && data.firstDataDate && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          은행 자료가 <b>{data.firstDataDate}</b> 부터만 올라와 있어 그 전 날은 전일잔액·입금이 비어 있습니다.
          인터넷뱅킹에서 <b>{month}-01 부터의 기간 거래내역</b>과 <b>전월 말일 잔액조회</b> 파일을 받아 「은행 파일 올리기」로 올리면 채워집니다.
        </p>
      )}
      {minClose && Number(minClose.bankClose) < 0 && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <b>{minClose.date}</b> 계좌 잔액이 <b>{money(minClose.bankClose)}</b> 로 모자랄 것으로 계산됩니다.
        </p>
      )}
      {/* 머리글(날짜) 고정 · 화면 높이에 맞춤 — 왼쪽 「구분」 칸 위에 몸통 고정 칸이 덮이지 않게 머리글을 한 단 위로(z-20). */}
      <FitBox deps={[data, loading, open]} className={`rounded-lg border ${loading ? 'opacity-50' : ''}`}>
        <table className="text-xs">
          <thead className="z-20">{head}</thead>
          <tbody>
            {block('[계좌]', BANK_ROWS)}
            {block('[현금]', CASH_ROWS)}
            <tr className="bg-canvas">
              <td className="sticky left-0 z-10 bg-canvas px-2 py-1 font-semibold">
                <button className="underline" onClick={() => setOpen((v) => !v)}>시재계정별 출금 {open ? '접기' : `펼치기(${data?.accounts.length ?? 0})`}</button>
              </td>
              <td colSpan={days.length + 1} />
            </tr>
            {open && data?.accounts.map((a) => (
              <tr key={a.name} className="border-b">
                <td className={`sticky left-0 z-10 whitespace-nowrap bg-white px-2 py-1 ${a.cash ? 'text-emerald-800' : ''} ${a.excluded ? 'text-ink-faint' : ''}`}
                  title={a.excluded ? '계좌간이체처럼 시재계정이 빈 줄 — 시트(시제예측)와 같이 출금 합계에서 뺍니다' : undefined}>{a.name}</td>
                {days.map((d) => (
                  <td key={d.date} className="whitespace-nowrap px-2 text-right">{a.byDay[d.date] ? money(a.byDay[d.date]) : ''}</td>
                ))}
                <td className="whitespace-nowrap px-2 text-right font-medium">{money(a.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </FitBox>
    </div>
  );
}

function CashCountForm({ corps, defaultCorp, onSaved }: { corps: AcctCorp[]; defaultCorp: number | null; onSaved: () => void }) {
  const [corpId, setCorpId] = useState<number>(defaultCorp ?? corps.find((c) => !c.parent_corp_id)?.corp_id ?? 0);
  const [date, setDate] = useState(kstToday());
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'cash' | 'bank'>('cash');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.listCashCounts>>['rows']>([]);
  const [nonce, setNonce] = useState(0);

  useEffect(() => { api.listCashCounts({ corpId: null }).then((r) => setRows(r.rows)).catch(() => undefined); }, [nonce]);

  async function save() {
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await api.saveCashCount({ corpId, date, kind, amount: amount.replace(/[,\s원]/g, '') || null, memo });
      setMsg(r.removed ? '그 날 값을 지웠습니다.' : '저장했습니다.');
      setAmount(''); setMemo(''); setNonce((n) => n + 1); onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card space-y-2 p-3 text-sm">
      <p className="text-xs text-ink-soft"><b>현금</b>: 금고의 현금을 센 금액 → 그 날 현금 잔액의 기점. <b>계좌</b>: 그 날 법인 계좌 잔액 합(예전 시트 잔액 입력과 같음) → 은행 파일이 없을 때 계좌 잔액의 기점. 월말 날짜로 적으면 다음 달 1일의 전일잔액이 됩니다. 같은 날 다시 적으면 덮어쓰고, 금액을 비우고 저장하면 지웁니다.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-40" value={corpId} onChange={(e) => setCorpId(Number(e.target.value))}>
          {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
        </select>
        <select className="field w-24" value={kind} onChange={(e) => setKind(e.target.value as 'cash' | 'bank')}>
          <option value="cash">현금</option>
          <option value="bank">계좌</option>
        </select>
        <input type="date" className="field w-40" value={date} onChange={(e) => setDate(e.target.value)} />
        <MoneyInput className="w-40" placeholder={kind === 'cash' ? '현금 금액' : '계좌 잔액 합'} value={amount} onChange={setAmount} />
        <input className="field w-56" placeholder="memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        {msg && <span className="text-emerald-700">{msg}</span>}
      </div>
      {error && <ErrorBox message={error} />}
      {rows.length > 0 && (
        <div className="max-h-32 overflow-auto text-xs text-ink-soft">
          {rows.map((r) => <div key={r.cash_count_id}>{r.count_date} · {r.corp_name} · {r.kind === 'bank' ? '계좌' : '현금'} {money(r.amount)} {r.memo ? `· ${r.memo}` : ''}</div>)}
        </div>
      )}
    </div>
  );
}
